import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '~/api/trpc';
import { Edit, Globe, Plus, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type GiftCardTemplateFormData = {
    name: string;
    valueAmount: string;
    type: 'USE_EVERYWHERE' | 'USE_LIMITED_TREATMENT_VARIANTS';
    isOnline: boolean;
    treatmentVariantIds: string[];
};

const emptyFormData: GiftCardTemplateFormData = {
    name: '',
    valueAmount: '',
    type: 'USE_EVERYWHERE',
    isOnline: false,
    treatmentVariantIds: [],
};

const GiftCardTemplatesEditor = () => {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
    const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
    const [formData, setFormData] = useState<GiftCardTemplateFormData>(emptyFormData);
    const [variantSearch, setVariantSearch] = useState('');

    const utils = trpc.useUtils();

    const { data: templatesData, isLoading } = trpc.giftCardTemplates.getAll.useQuery();
    const { data: variantSearchData } = trpc.giftCardTemplates.searchTreatmentVariants.useQuery(
        { search: variantSearch },
        { enabled: variantSearch.length >= 2 }
    );

    const createMutation = trpc.giftCardTemplates.create.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarten-Vorlage erstellt');
            utils.giftCardTemplates.getAll.invalidate();
            setIsCreateDialogOpen(false);
            setFormData(emptyFormData);
            setVariantSearch('');
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const updateMutation = trpc.giftCardTemplates.update.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarten-Vorlage aktualisiert');
            utils.giftCardTemplates.getAll.invalidate();
            setIsEditDialogOpen(false);
            setEditingTemplateId(null);
            setFormData(emptyFormData);
            setVariantSearch('');
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const deleteMutation = trpc.giftCardTemplates.delete.useMutation({
        onSuccess: () => {
            toast.success('Gutscheinkarten-Vorlage gelöscht');
            utils.giftCardTemplates.getAll.invalidate();
            setIsDeleteDialogOpen(false);
            setDeletingTemplateId(null);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const handleCreate = () => {
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!formData.valueAmount || parseFloat(formData.valueAmount) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Wert ein');
            return;
        }
        if (formData.type === 'USE_LIMITED_TREATMENT_VARIANTS' && formData.treatmentVariantIds.length === 0) {
            toast.error('Bitte wählen Sie mindestens eine Behandlungsvariante aus');
            return;
        }

        createMutation.mutate({
            name: formData.name.trim(),
            valueAmount: parseFloat(formData.valueAmount),
            type: formData.type,
            isOnline: formData.isOnline,
            treatmentVariantIds: formData.type === 'USE_LIMITED_TREATMENT_VARIANTS' ? formData.treatmentVariantIds : [],
        });
    };

    const handleUpdate = () => {
        if (!editingTemplateId) {
            return;
        }
        if (!formData.name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        if (!formData.valueAmount || parseFloat(formData.valueAmount) <= 0) {
            toast.error('Bitte geben Sie einen gültigen Wert ein');
            return;
        }
        if (formData.type === 'USE_LIMITED_TREATMENT_VARIANTS' && formData.treatmentVariantIds.length === 0) {
            toast.error('Bitte wählen Sie mindestens eine Behandlungsvariante aus');
            return;
        }

        updateMutation.mutate({
            id: editingTemplateId,
            name: formData.name.trim(),
            valueAmount: parseFloat(formData.valueAmount),
            type: formData.type,
            isOnline: formData.isOnline,
            treatmentVariantIds: formData.type === 'USE_LIMITED_TREATMENT_VARIANTS' ? formData.treatmentVariantIds : [],
        });
    };

    const handleDelete = () => {
        if (!deletingTemplateId) {
            return;
        }
        deleteMutation.mutate({ id: deletingTemplateId });
    };

    const openEditDialog = (template: NonNullable<typeof templatesData>['data'][number]) => {
        setEditingTemplateId(template.id);
        setFormData({
            name: template.name,
            valueAmount: template.valueAmount.toString(),
            type: template.type,
            isOnline: template.isOnline,
            treatmentVariantIds: template.validTreatmentVariants.map((v) => v.treatmentVariant.id),
        });
        setVariantSearch('');
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (templateId: string) => {
        setDeletingTemplateId(templateId);
        setIsDeleteDialogOpen(true);
    };

    const addVariant = (variantId: string) => {
        if (!formData.treatmentVariantIds.includes(variantId)) {
            setFormData({ ...formData, treatmentVariantIds: [...formData.treatmentVariantIds, variantId] });
        }
        setVariantSearch('');
    };

    const removeVariant = (variantId: string) => {
        setFormData({
            ...formData,
            treatmentVariantIds: formData.treatmentVariantIds.filter((id) => id !== variantId),
        });
    };

    const templates = templatesData?.data || [];
    const searchVariants = variantSearchData?.data || [];

    const formatCurrency = (value: number | string) => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
    };

    // Get variant names for display (from current templates data or search results)
    const getVariantName = (variantId: string): string => {
        for (const template of templates) {
            const found = template.validTreatmentVariants.find((v) => v.treatmentVariant.id === variantId);
            if (found) {
                return found.treatmentVariant.name;
            }
        }
        const found = searchVariants.find((v) => v.id === variantId);
        if (found) {
            return found.name;
        }
        return variantId;
    };

    const typeLabel = (type: string) => {
        if (type === 'USE_EVERYWHERE') {
            return 'Überall einsetzbar';
        }
        return 'Eingeschränkt';
    };

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    const variantPickerContent = (
        <>
            {formData.type === 'USE_LIMITED_TREATMENT_VARIANTS' && (
                <div className='space-y-3'>
                    <Label>Gültige Behandlungsvarianten</Label>
                    <p className='text-xs text-muted-foreground'>
                        Wählen Sie die Behandlungsvarianten, für die diese Gutscheinkarte eingesetzt werden kann.
                    </p>

                    {formData.treatmentVariantIds.length > 0 && (
                        <div className='flex flex-wrap gap-1.5'>
                            {formData.treatmentVariantIds.map((variantId) => (
                                <Badge key={variantId} variant='secondary' className='gap-1'>
                                    {getVariantName(variantId)}
                                    <button
                                        type='button'
                                        onClick={() => removeVariant(variantId)}
                                        className='ml-1 hover:text-destructive'
                                    >
                                        <X className='h-3 w-3' />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}

                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder='Behandlungsvariante suchen...'
                            value={variantSearch}
                            onChange={(e) => setVariantSearch(e.target.value)}
                            className='pl-9'
                        />
                    </div>
                    {variantSearch.length >= 2 && (
                        <div className='max-h-36 overflow-y-auto border rounded bg-white'>
                            {searchVariants
                                .filter((v) => !formData.treatmentVariantIds.includes(v.id))
                                .map((variant) => (
                                    <div
                                        key={variant.id}
                                        className='p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0'
                                        onClick={() => addVariant(variant.id)}
                                    >
                                        <p className='text-sm font-medium'>{variant.name}</p>
                                        <p className='text-xs text-muted-foreground'>{variant.treatment.name}</p>
                                    </div>
                                ))}
                            {searchVariants.filter((v) => !formData.treatmentVariantIds.includes(v.id)).length === 0 && (
                                <p className='text-sm text-muted-foreground py-3 text-center'>
                                    Keine Varianten gefunden.
                                </p>
                            )}
                        </div>
                    )}
                    {variantSearch.length === 1 && (
                        <p className='text-xs text-muted-foreground'>Mindestens 2 Zeichen eingeben.</p>
                    )}
                </div>
            )}
        </>
    );

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Gutscheinkarten-Vorlagen definieren den Wert und die Einschränkungen für Gutscheinkarten.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neue Vorlage
                </Button>
            </div>

            {templates.length === 0 ? (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='py-12 text-center text-muted-foreground'>
                        Noch keine Vorlagen erstellt. Klicken Sie auf &quot;Neue Vorlage&quot; um zu beginnen.
                    </div>
                </div>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Name</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Typ</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Wert</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Varianten</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-center'>Aktive Karten</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {templates.map((template) => (
                                    <TableRow key={template.id} className='hover:bg-gray-50 transition-colors'>
                                        <TableCell className='font-medium'>
                                            <div className='flex items-center gap-2'>
                                                {template.name}
                                                {template.isOnline && (
                                                    <Badge variant='outline' className='gap-1 text-blue-600 border-blue-300'>
                                                        <Globe className='h-3 w-3' />
                                                        Online
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={template.type === 'USE_EVERYWHERE' ? 'default' : 'secondary'}>
                                                {typeLabel(template.type)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className='text-right'>
                                            {formatCurrency(template.valueAmount)}
                                        </TableCell>
                                        <TableCell className='text-center'>
                                            {template.type === 'USE_LIMITED_TREATMENT_VARIANTS'
                                                ? template.validTreatmentVariants.length
                                                : '-'}
                                        </TableCell>
                                        <TableCell className='text-center'>
                                            {template._count.giftCards > 0 ? (
                                                <span className='text-sm text-green-600'>{template._count.giftCards}</span>
                                            ) : (
                                                <span className='text-sm text-muted-foreground'>0</span>
                                            )}
                                        </TableCell>
                                        <TableCell className='text-right'>
                                            <div className='flex justify-end gap-1'>
                                                <Button variant='ghost' size='icon' onClick={() => openEditDialog(template)}>
                                                    <Edit className='h-4 w-4' />
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={() => openDeleteDialog(template.id)}
                                                    disabled={template._count.giftCards > 0}
                                                >
                                                    <Trash2 className='h-4 w-4' />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className='max-w-lg max-h-[80vh] overflow-hidden flex flex-col'>
                    <DialogHeader>
                        <DialogTitle>Neue Gutscheinkarten-Vorlage</DialogTitle>
                        <DialogDescription>
                            Erstellen Sie eine neue Vorlage für Gutscheinkarten.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='flex-1 overflow-auto space-y-4'>
                        <div className='space-y-2'>
                            <Label>Name</Label>
                            <Input
                                placeholder='z.B. Gutscheinkarte 100€'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Wert (€)</Label>
                            <Input
                                type='number'
                                step='0.01'
                                min='0'
                                placeholder='100.00'
                                value={formData.valueAmount}
                                onChange={(e) => setFormData({ ...formData, valueAmount: e.target.value })}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Typ</Label>
                            <Select
                                value={formData.type}
                                onValueChange={(value) => setFormData({
                                    ...formData,
                                    type: value as GiftCardTemplateFormData['type'],
                                    treatmentVariantIds: value === 'USE_EVERYWHERE' ? [] : formData.treatmentVariantIds,
                                })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='USE_EVERYWHERE'>Überall einsetzbar</SelectItem>
                                    <SelectItem value='USE_LIMITED_TREATMENT_VARIANTS'>Nur für bestimmte Behandlungen</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='flex items-center space-x-2'>
                            <Checkbox
                                id='create-isOnline'
                                checked={formData.isOnline}
                                onCheckedChange={(checked) => setFormData({ ...formData, isOnline: !!checked })}
                            />
                            <Label htmlFor='create-isOnline' className='cursor-pointer'>
                                Online-Gutschein
                            </Label>
                        </div>
                        {variantPickerContent}
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsCreateDialogOpen(false);
                            setFormData(emptyFormData);
                            setVariantSearch('');
                        }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCreate} disabled={createMutation.isPending}>
                            {createMutation.isPending ? 'Erstelle...' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
                setIsEditDialogOpen(open);
                if (!open) {
                    setEditingTemplateId(null);
                    setFormData(emptyFormData);
                    setVariantSearch('');
                }
            }}>
                <DialogContent className='max-w-lg max-h-[80vh] overflow-hidden flex flex-col'>
                    <DialogHeader>
                        <DialogTitle>Gutscheinkarten-Vorlage bearbeiten</DialogTitle>
                        <DialogDescription>
                            Bearbeiten Sie die Vorlage für Gutscheinkarten.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='flex-1 overflow-auto space-y-4'>
                        <div className='space-y-2'>
                            <Label>Name</Label>
                            <Input
                                placeholder='z.B. Gutscheinkarte 100€'
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Wert (€)</Label>
                            <Input
                                type='number'
                                step='0.01'
                                min='0'
                                placeholder='100.00'
                                value={formData.valueAmount}
                                onChange={(e) => setFormData({ ...formData, valueAmount: e.target.value })}
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label>Typ</Label>
                            <Select
                                value={formData.type}
                                onValueChange={(value) => setFormData({
                                    ...formData,
                                    type: value as GiftCardTemplateFormData['type'],
                                    treatmentVariantIds: value === 'USE_EVERYWHERE' ? [] : formData.treatmentVariantIds,
                                })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='USE_EVERYWHERE'>Überall einsetzbar</SelectItem>
                                    <SelectItem value='USE_LIMITED_TREATMENT_VARIANTS'>Nur für bestimmte Behandlungen</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className='flex items-center space-x-2'>
                            <Checkbox
                                id='edit-isOnline'
                                checked={formData.isOnline}
                                onCheckedChange={(checked) => setFormData({ ...formData, isOnline: !!checked })}
                            />
                            <Label htmlFor='edit-isOnline' className='cursor-pointer'>
                                Online-Gutschein
                            </Label>
                        </div>
                        {variantPickerContent}
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsEditDialogOpen(false);
                            setEditingTemplateId(null);
                            setFormData(emptyFormData);
                            setVariantSearch('');
                        }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                            {updateMutation.isPending ? 'Speichere...' : 'Speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Vorlage löschen</DialogTitle>
                        <DialogDescription>
                            Sind Sie sicher, dass Sie diese Gutscheinkarten-Vorlage löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => {
                            setIsDeleteDialogOpen(false);
                            setDeletingTemplateId(null);
                        }}>
                            Abbrechen
                        </Button>
                        <Button variant='destructive' onClick={handleDelete} disabled={deleteMutation.isPending}>
                            {deleteMutation.isPending ? 'Lösche...' : 'Löschen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default GiftCardTemplatesEditor;
